import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

const useSlidingWindow = ({
  data = [],
  scrollContainerRef,
  pageSize = 30,
  overlap = 5,
  rootMargin = "100px",
}) => {
  const [visibleItems, setVisibleItems] = useState([]);
  const rangeRef = useRef({ startIndex: 0, endIndex: pageSize });
  const anchorRef = useRef({ id: null, offsetTop: 0 });

  // Create a Map to store refs to each list item DOM node
  const itemRefs = useRef(new Map());

  // Sentinels
  const topObserverRef = useRef(null);
  const bottomObserverRef = useRef(null);

  // Use a ref for the handler so the IntersectionObserver doesn't need
  // to be re-created when state/props change.
  const internalDataRef = useRef(data);
  internalDataRef.current = data;

  const canLoadMoreUp = rangeRef.current.startIndex > 0;
  const canLoadMoreDown = rangeRef.current.endIndex < data.length;

  const setItemRef = useCallback(
    (id) => (el) => {
      if (el) {
        itemRefs.current.set(id, el);
      } else {
        itemRefs.current.delete(id);
      }
    },
    []
  );

  const handlePageChange = useCallback(
    (direction) => {
      const container = scrollContainerRef?.current;
      if (!container) {
        return;
      }

      const { startIndex, endIndex } = rangeRef.current;
      const currentData = internalDataRef.current;

      if (direction === "up" && startIndex > 0) {
        // 1. Anchor the current top item to prevent scroll jump
        const firstItemId = visibleItems[0]?.id;
        const firstItemEl = itemRefs.current.get(firstItemId);
        if (firstItemEl) {
          anchorRef.current = {
            id: firstItemId,
            offsetTop: firstItemEl.offsetTop - container.scrollTop,
          };
        }

        // 2. Calculate new range
        const newStart = Math.max(0, startIndex - (pageSize - overlap));
        const newEnd = Math.max(pageSize, startIndex + overlap);

        rangeRef.current = { startIndex: newStart, endIndex: newEnd };
        setVisibleItems(currentData.slice(newStart, newEnd));
      } else if (direction === "down" && endIndex < currentData.length) {
        // 1. Calculate new range
        const newStart = Math.max(0, endIndex - overlap);
        const newEnd = Math.min(currentData.length, newStart + pageSize);

        rangeRef.current = { startIndex: newStart, endIndex: newEnd };
        setVisibleItems(currentData.slice(newStart, newEnd));
      }
    },
    [visibleItems, scrollContainerRef, pageSize, overlap]
  );

  // Handle Scroll Anchoring
  useLayoutEffect(() => {
    if (anchorRef.current.id && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const anchorEl = itemRefs.current.get(anchorRef.current.id);
      if (anchorEl) {
        container.scrollTop = anchorEl.offsetTop - anchorRef.current.offsetTop;
      }
      anchorRef.current = { id: null, offsetTop: 0 };
    }
  }, [visibleItems, scrollContainerRef]);

  // The Observer: Only setup ONCE
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          if (entry.target === topObserverRef.current) {
            console.log("top intersecting");
            handlePageChange("up");
          } else if (entry.target === bottomObserverRef.current) {
            console.log("bottom intersecting");
            handlePageChange("down");
          }
        });
      },
      { root: container, rootMargin, threshold: 0 }
    );

    const top = topObserverRef.current;
    const bottom = bottomObserverRef.current;

    if (top) {
      observer.observe(top);
    }
    if (bottom) {
      observer.observe(bottom);
    }

    return () => observer.disconnect();
    // dependency array is minimal to prevent observer "flicker"
  }, [scrollContainerRef, handlePageChange, rootMargin]);

  // Sync with external data changes
  useEffect(() => {
    const { startIndex, endIndex } = rangeRef.current;
    setVisibleItems(data.slice(startIndex, Math.max(endIndex, pageSize)));
  }, [data, pageSize]);

  return {
    visibleItems,
    topObserverRef,
    bottomObserverRef,
    canLoadMoreUp,
    canLoadMoreDown,
    setItemRef,
  };
};

export default useSlidingWindow;
